import { Button } from "@sellar-io/components";
import { RelationshipCommentListResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, SpinnerLegacy } from "../";
import { useEvents } from "../../hooks";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useRelationshipCommentCreate } from "../../hooks/api/v2/relationshipComments/useRelationshipCommentCreate";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { BadPayerWarning } from "../badPayerWarning/BadPayerWarning";
import { ErrorMessage } from "../errorMessage/ErrorMessage";
import { InfiniteList } from "../infiniteLoading/InfiniteList";
import { EnablePushNotificationAlert } from "../pushNotification/EnablePushNotificationAlert";
import { Comment } from "./Comment";
import styles from "./Comments.module.css";
import { ChatInput } from "../chatInput/ChatInput";

dayjs.extend(relativeTime);

export const RelationshipComments = ({
  relationshipId,
  mutation,
  commentsQuery,
  flaggedAsBadPayer,
  className,
}: {
  title?: ReactNode;
  relationshipId: number;
  mutation: ReturnType<typeof useRelationshipCommentCreate>;
  commentsQuery: UseInfiniteQueryResult<
    InfiniteData<RelationshipCommentListResponseType>,
    Error
  >;
  flaggedAsBadPayer?: boolean;
  className?: string;
}) => {
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const [newComment, setNewComment] = useState("");

  if (commentsQuery.error)
    return <ErrorMessage>{commentsQuery.error.message}</ErrorMessage>;

  if (commentsQuery.isLoading) return <SpinnerLegacy />;

  return (
    <div
      className={twMerge(
        "flex h-[calc(100vh-var(--nav-height))] flex-col-reverse justify-between overflow-auto bg-white",
        className,
      )}
    >
      {mutation.isPending ? (
        <SpinnerLegacy />
      ) : (
        <div className={"sticky bottom-0 flex flex-col items-center gap-2 p-4"}>
          <EnablePushNotificationAlert
            title="Enable direct message notifications?"
            message="Receive notifications for your conversations on Sellar in your browser."
            className="mx-4 w-full"
            showSettings={true}
          />
          <BadPayerWarning flaggedAsBadPayer={flaggedAsBadPayer} />
          <div className={"flex w-full items-end gap-2"}>
            <ChatInput
              value={newComment}
              setValue={(value) => setNewComment(value)}
              placeholder="Type a message"
            />
            <Button
              disabled={newComment === ""}
              onClick={async () => {
                mutation.mutate(
                  {
                    RelationshipId: relationshipId,
                    message: newComment,
                  },
                  {
                    onSuccess: () => {
                      setNewComment("");
                      track("Comment Created", {
                        businessName: business?.name,
                        businessId: business?.id,
                        message: newComment,
                        kind: "message",
                      });
                    },
                  },
                );
              }}
            >
              <Icon icon={"SEND"} />
            </Button>
          </div>
        </div>
      )}
      <InfiniteList
        query={commentsQuery}
        className={"flex flex-col-reverse px-4 pt-4"}
      >
        {mutation.isError && (
          <span>{apiV2AssertError(mutation.error)?.message}</span>
        )}
        <div className={"flex flex-col-reverse gap-2"}>
          {commentsQuery.data?.pages[0]?.length ? (
            commentsQuery.data.pages.map((page) =>
              page.map((comment, i) => (
                <Comment
                  key={i}
                  comment={{
                    id: Number(comment.id),
                    createdAt: comment.createdAt,
                    message: comment.message,
                  }}
                  createdBy={{
                    businessUserId: comment.BusinessUserId,
                    userId: comment.BusinessUser.User.id,
                    userName: comment.BusinessUser.User.name,
                    businessId: comment.BusinessUser.Business.id,
                    businessName: comment.BusinessUser.Business.name,
                    userProfileImageUrl:
                      comment.BusinessUser.User.profileImageUrl,
                    jobTitle: comment.BusinessUser.jobTitle,
                  }}
                />
              )),
            )
          ) : (
            <div className={styles.commentContainer}>No messages</div>
          )}
        </div>
      </InfiniteList>
    </div>
  );
};
